'use client';

/**
 * ContentDock — the bottom edge of the dashboard's CONTENT area (BL-1905).
 *
 * Floating chrome that belongs to the page — the bulk-action bar and the toasts
 * above it — has to centre between the side rail and the right edge, not on the
 * whole window. The rail collapses (w-64 ↔ w-16) and hides below `md`, so a
 * `position: fixed` element cannot know where the content starts without
 * measuring. The dock gets it from layout instead: `DashboardShell` renders it
 * around everything right of the rail, and anything portalled into it inherits
 * that box's geometry, including through the rail's collapse animation.
 *
 * That box includes the docked lot panel's rail (BUG-1864): with a panel open
 * the bar still centres on — and sizes itself against — the full width, and
 * overlays the panel's foot where it needs the room, rather than squeezing into
 * the page column beside it (and pushing actions under More there when it
 * needn't, BL-2669).
 *
 * Two layers, both with no height of their own:
 *  - the toaster, mounted here once for the whole admin. `contain: layout`
 *    makes the dock the containing block for Sonner's `position: fixed` stack,
 *    so the stack centres on the column too;
 *  - the bar host, `useContentDock()`'s return value, which `BulkBarSlot`
 *    portals into (24px up — the handoff's bottom offset).
 *
 * `--dock-toast-bottom` lifts the toast stack above the bar slot: the bar
 * host's measured height plus the 24px bottom offset and a 10px gap, or 24px
 * when the host is empty (a page with no bulk bar). A one-line bar is 50px, so
 * 84px; 128px while its error pill shows above it (+36px pill + the 8px
 * `cluster` gap); 66px+ on a phone, where the bar's tiles are 52px (BL-2669),
 * which is why it is measured rather than a fixed step per state. Read by
 * `AdminToaster`'s offset.
 *
 * Keyed on the bar being MOUNTED, not open, on purpose — a closed bar keeps its
 * layout box, so the measurement doesn't drop while it slides away. A bulk action's success
 * toast fires in the same tick the selection clears, so the bar starts sliding
 * away as the toast appears. Keyed on "open", the toast would land where the
 * bar was, over the bar still fading out, instead of above it (AC 7). A page
 * with a bulk table keeps its toasts in the slot the handoff puts them in.
 */

import { createContext, useContext, useState, type CSSProperties, type ReactNode } from 'react';
import AdminToaster from './AdminToaster';
import { useElementHeight } from './use-container-width';

/** The bar slot's offset from the dock's bottom edge (`bottom-6`), in px. */
const DOCK_BOTTOM_PX = 24;
/** The gap between the top of the bar host and the lowest toast, in px. */
const TOAST_GAP_PX = 10;

/**
 * The toast stack's bottom offset for a bar host of the given height.
 *
 * @param hostHeight The bar host's content height, in px (0 when empty).
 * @returns The `--dock-toast-bottom` value, e.g. "84px".
 */
function dockToastBottom(hostHeight: number): string {
    return `${hostHeight > 0 ? DOCK_BOTTOM_PX + hostHeight + TOAST_GAP_PX : DOCK_BOTTOM_PX}px`;
}

const ContentDockContext = createContext<HTMLElement | null>(null);

/**
 * The element floating page chrome portals into, or `null` outside a dashboard
 * shell (tests, the sign-in pages) — a caller then renders in place.
 *
 * @returns The dock's bar host, or null.
 */
export function useContentDock(): HTMLElement | null {
    return useContext(ContentDockContext);
}

/**
 * Provides the dock to `children` and renders it after them.
 *
 * @param props.children Everything right of the nav rail: the page column
 *   (with its `<main>` scroller) and the docked-panel rail.
 * @returns The children plus the dock layer.
 */
export default function ContentDock({ children }: { children: ReactNode }) {
    // A callback ref into state, so descendants re-render with the host once it
    // exists — the bar only ever opens after a selection, long after this.
    const [barHost, setBarHost] = useState<HTMLDivElement | null>(null);
    const hostHeight = useElementHeight(barHost);
    return (
        <ContentDockContext.Provider value={barHost}>
            {children}
            <div
                data-testid="content-dock"
                className="pointer-events-none absolute inset-x-0 bottom-0 z-40 h-0 [contain:layout] print:hidden"
                // Toast lift — measurements in the header comment.
                style={{ '--dock-toast-bottom': dockToastBottom(hostHeight) } as CSSProperties}
            >
                <AdminToaster />
                <div
                    ref={setBarHost}
                    data-testid="content-dock-bars"
                    className="absolute inset-x-0 bottom-6 flex flex-col items-center px-4"
                />
            </div>
        </ContentDockContext.Provider>
    );
}
